import { ArrowLeft } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const ProcessedResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { apiResponse } = location.state || {};
  const [zoomLevel, setZoomLevel] = useState(50);
  const [brightness, setBrightness] = useState(100);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (!apiResponse || !apiResponse.images || apiResponse.images.length === 0) {
      const timer = setTimeout(() => {
        alert("No processed images available. Please try again.");
        navigate("/ai-model");
      }, 2000);
      return () => clearTimeout(timer);
    }
    
    setLoading(false);
    if (apiResponse.images.length > 0) {
      setSelectedImage(0);
    }
  }, [apiResponse, navigate]);

  const applyBrightnessToImage = (base64Image, brightnessValue) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Apply brightness filter
        ctx.filter = `brightness(${brightnessValue}%)`;
        ctx.drawImage(img, 0, 0);
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          resolve(url);
        }, 'image/png');
      };
      img.src = base64Image;
    });
  };

  const handleDownload = async (imageUrl, index, applyBrightness = true) => {
    let downloadUrl = imageUrl;
    
    if (applyBrightness && brightness !== 100) {
      downloadUrl = await applyBrightnessToImage(imageUrl, brightness);
    }
    
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `processed-image-${index + 1}.png`;
    link.click();
    
    // Clean up blob URL if created
    if (downloadUrl !== imageUrl) {
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
    }
  };

  const handleBack = () => {
    navigate("/ai-model");
  };

  const handleDownloadAll = () => {
    apiResponse.images.forEach((image, index) => {
      setTimeout(() => {
        const imageSource = getImageSource(image);
        handleDownload(imageSource, index, true);
      }, index * 500);
    });
  };

  // Helper to check if image is a URL
  const isImageUrl = (image) => {
    return typeof image === 'string' && (image.startsWith('http://') || image.startsWith('https://'));
  };

  // Helper to get image source for display
  const getImageSource = (image) => {
    if (!image || typeof image !== 'string') return '';
    if (isImageUrl(image)) return image; // URL (including signed URLs)
    if (image.startsWith('data:image')) return image; // Data URL
    return `data:image/png;base64,${image}`; // Plain base64
  };

  const handleZoomChange = (e) => {
    setZoomLevel(parseInt(e.target.value, 10));
  };

  const handleBrightnessChange = (e) => {
    setBrightness(parseInt(e.target.value, 10));
  };

  return (
    <div className="flex-1 h-full w-full mx-auto overflow-auto relative z-10 bg-gradient-to-br from-indigo-50 to-blue-100">
      {/* Header */}
      <header className="w-full mx-auto py-3 sm:py-4 px-3 sm:px-4 bg-white shadow-sm border-b border-blue-100 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-center sm:justify-start gap-2 sm:gap-3 flex-1">
            <button
              onClick={handleBack}
              className="p-2 sm:p-2 rounded-full hover:bg-blue-50 transition-colors text-blue-600 active:bg-blue-100"
              aria-label="Go back"
            >
              <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
            </button>
            <div className="text-center sm:text-left">
              <h1 className="text-lg sm:text-xl md:text-2xl font-semibold bg-gradient-to-r from-violet-600 to-blue-500 text-transparent bg-clip-text">
                AI Model Results
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 -mt-1">
                {apiResponse?.images?.length || 0} processed images
              </p>
            </div>
          </div>
          <button
            onClick={handleDownloadAll}
            disabled={!apiResponse?.images?.length}
            className="hidden sm:flex bg-gradient-to-r from-violet-600 to-blue-500 hover:from-violet-700 hover:to-blue-600 disabled:from-gray-400 disabled:to-gray-500 text-white px-4 py-2 rounded-lg transition-colors shadow-md items-center justify-center font-medium text-sm disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Download All ({apiResponse?.images?.length || 0})
          </button>
        </div>
      </header>
      
      <main className="w-full px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] w-full">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-t-4 border-b-4 border-violet-600 mb-4"></div>
            <p className="text-base sm:text-lg text-gray-700 font-medium mb-2">Processing your images...</p>
            <p className="text-sm text-gray-500 text-center max-w-xs">This may take a few moments depending on the number of images</p>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {/* Image Grid and Preview Section */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              {/* Thumbnail Grid */}
              <div className="bg-white/90 backdrop-blur-sm p-3 sm:p-4 pb-2 sm:pb-3 xl:pb-2 rounded-xl shadow-lg border border-violet-100 xl:col-span-1 order-2 xl:order-1">
                <h2 className="text-sm sm:text-base font-semibold text-gray-800 mb-3 pb-2 border-b border-violet-100">
                  Result Images ({apiResponse?.images?.length || 0})
                </h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-2 gap-2 sm:gap-3 xl:gap-4 pr-1 content-start max-h-[35vh] xl:max-h-[40vh]">
                  {apiResponse?.images && apiResponse.images.map((image, index) => (
                    <div 
                      key={index} 
                      className={`cursor-pointer rounded-lg overflow-hidden transition-all transform hover:scale-[1.02] active:scale-95 ${
                        selectedImage === index ? 'ring-2 ring-violet-500 shadow-lg' : 'border border-gray-200 hover:border-violet-300'
                      }`}
                      onClick={() => setSelectedImage(index)}
                    >
                      <div className="relative aspect-square xl:aspect-[4/5]">
                        <img
                          src={getImageSource(image)}
                          alt={`Processed ${index + 1}`}
                          className="w-full h-full object-cover object-top"
                        />
                        {selectedImage === index && (
                          <div className="absolute top-1 right-1 bg-violet-500 h-4 w-4 sm:h-5 sm:w-5 rounded-full flex items-center justify-center shadow-md">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected Image Preview */}
              <div className="bg-white/90 backdrop-blur-sm p-3 sm:p-4 rounded-xl shadow-lg border border-violet-100 xl:col-span-2 order-1 xl:order-2">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 pb-2 border-b border-violet-100 gap-2">
                  <h2 className="text-sm sm:text-base font-semibold text-gray-800">
                    {selectedImage !== null ? `Preview: Result ${selectedImage + 1}` : 'Select an image to preview'}
                  </h2>
                  {selectedImage !== null && (
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      {/* Zoom Controls */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-600">Zoom:</span>
                        <input
                          type="range"
                          min="25"
                          max="150"
                          value={zoomLevel}
                          onChange={handleZoomChange}
                          className="w-20 sm:w-24 h-1.5 accent-violet-600"
                        />
                        <span className="min-w-10 text-xs sm:text-sm bg-violet-100 px-2 py-1 rounded text-violet-700 font-medium">
                          {zoomLevel}%
                        </span>
                      </div>
                      {/* Brightness Controls */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-600">Brightness:</span>
                        <input
                          type="range"
                          min="50"
                          max="150"
                          value={brightness}
                          onChange={handleBrightnessChange}
                          className="w-20 sm:w-24 h-1.5 accent-violet-600"
                        />
                        <span className="min-w-10 text-xs sm:text-sm bg-violet-100 px-2 py-1 rounded text-violet-700 font-medium">
                          {brightness}%
                        </span>
                      </div>
                      {/* Download Button */}
                      <button
                        onClick={() => handleDownload(getImageSource(apiResponse.images[selectedImage]), selectedImage)}
                        className="bg-gradient-to-r from-violet-600 to-blue-500 hover:from-violet-700 hover:to-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm transition-colors flex items-center justify-center shadow-md font-medium"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Download
                      </button>
                    </div>
                  )}
                </div>
                {selectedImage !== null ? (
                  <div className="flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg overflow-hidden min-h-[40vh] sm:min-h-[50vh] xl:min-h-[70vh] border border-violet-100">
                    <div className="overflow-auto w-full h-full max-h-[40vh] sm:max-h-[50vh] xl:max-h-[70vh] p-2">
                      <img
                        src={getImageSource(apiResponse.images[selectedImage])}
                        alt={`Processed ${selectedImage + 1}`}
                        style={{ 
                          width: `${zoomLevel}%`, 
                          height: "auto",
                          maxWidth: "none",
                          filter: `brightness(${brightness}%)`
                        }}
                        className="transition-all duration-200 shadow-lg mx-auto"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg min-h-[40vh] sm:min-h-[50vh] xl:min-h-[70vh] border border-violet-100">
                    <div className="text-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-500 font-medium text-sm sm:text-base">Tap a thumbnail to preview</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};